import CreateButton from "../components/CreateButton"
import { useEffect, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import api from "../api"
import { ButtonStatus, FeedbackType } from "../common/constants"
import Button from "../components/Button"
import AlertDialog from "../components/dialogs/AlertDialog"
import Dialog from "../components/dialogs/Dialog"
import FeedbackEditor from "../components/dialogs/FeedbackEditor"
import FeedbacksCourses from "../components/FeedbacksCourses"
import FeedbacksLessons from "../components/FeedbacksLessons"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import { DialogStatus } from "../enums"
import styles from "./Feedbacks.module.css"

const Feedbacks = () => {
	const feedbackTypes = [{ id: FeedbackType.Course, label: "Corsi" }, { id: FeedbackType.Lesson, label: "Lezioni" }]
	const [filter, setFilter] = useState(feedbackTypes[0])
	const [feedbacks, setFeedbacks] = useState({ [FeedbackType.Course]: [], [FeedbackType.Lesson]: [] })
	const [loading, setLoading] = useState({ course: false, lesson: false })
	const [editingFeedback, setEditingFeedback] = useState(null)
	const [newFeedback, setNewFeedback] = useState(null)
	const [openDialog, setOpenDialog] = useState(false)
	const [saveStatus, setSaveStatus] = useState(null)
	const [disableSave, setDisableSave] = useState(false)
	const [courses, setCourses] = useState([])
	const [alertDialog, setAlertDialog] = useState({
		open: false,
		title: "",
		text: "",
		actions: [],
		status: DialogStatus.Default,
		timeout: null
	})

	const onFilterStateChange = (i) => {
		setFilter(feedbackTypes[i])
	}

	useEffect(() => {
		const getCourses = async () => {
			try {
				const courses = await api.get("/admin/courses")
				setCourses(courses)
			} catch (e) {
				console.error(e)
			}
		}

		getFeedbacksCourses().then(() => getCourses())
		getFeedbacksLessons()

		return () => {
			if (alertDialog.timeout) {
				clearTimeout(alertDialog.timeout)
			}
		}
	}, [])

	useEffect(() => {
		setOpenDialog(!!editingFeedback)
	}, [editingFeedback])


	const save = async () => {
		setSaveStatus(ButtonStatus.Loading)
		try {
			const response = editingFeedback ?
				await api.put(`/admin/feedbacks/${editingFeedback.id}`, newFeedback) :
				await api.post(`/admin/feedbacks`, newFeedback)

			setOpenDialog(false)
			setEditingFeedback(null)
			getFeedbacksCourses()
			getFeedbacksLessons()
			setSaveStatus(null)
		} catch (e) {
			setSaveStatus(ButtonStatus.Error)
			console.error(e)
		}
	}

	const getFeedbacksCourses = async () => {
		setLoading(l => ({ ...l, [FeedbackType.Course]: true }))
		try {
			const response = await api.get(`/admin/feedbacks/courses`)
			setFeedbacks(f => ({ ...f, [FeedbackType.Course]: response }))
		} catch (e) {
			console.error(e)
		}
		setLoading(l => ({ ...l, [FeedbackType.Course]: false }))
	}

	const getFeedbacksLessons = async () => {
		setLoading(l => ({ ...l, [FeedbackType.Lesson]: true }))
		try {
			const response = await api.get(`/admin/feedbacks/lessons`)
			setFeedbacks(f => ({ ...f, [FeedbackType.Lesson]: response }))
		} catch (e) {
			console.error(e)
		}
		setLoading(l => ({ ...l, [FeedbackType.Lesson]: false }))
	}

	const closeAlert = () => {
		setAlertDialog({
			open: false,
			title: "",
			text: "",
			status: DialogStatus.Default,
			timeout: null
		})
	}

	const askPermission = (feedback) => {
		setAlertDialog({
			open: true,
			text: "Eliminando questo modulo gli studenti non potranno più dare il loro feedback. Sei sicuro di voler continuare?",
			status: DialogStatus.Default,
			actions: [
				{ label: "ANNULLA", onClick: closeAlert },
				{ label: "ELIMINA", onClick: () => deleteFeedback(feedback), accentColor: "var(--sf-red)" }
			]
		})
	}

	const deleteFeedback = async (feedback) => {
		setAlertDialog(a => ({ ...a, status: DialogStatus.Loading }))
		try {
			await api.delete(`/admin/feedbacks/${feedback.id}`)
			setFeedbacks(f => {
				const updatedFeedbacks = f[filter.id].filter(fb => fb.id !== feedback.id)
				return { ...f, [filter.id]: updatedFeedbacks }
			})
			const timeout = setTimeout(closeAlert, 1500)
			setAlertDialog(a => ({ ...a, status: DialogStatus.Success, timeout }))
			// getFeebacks()
		} catch (e) {
			console.error(e)
			setAlertDialog(a => ({ ...a, status: DialogStatus.Error }))
		}
	}

	const duplicateFeedback = async (feedback) => {
		try {
			await api.post(`/admin/feedbacks`, {
				name: feedback.name,
				description: feedback.description,
				content: feedback.content,
				resource_type: feedback.resource_type,
				resource_id: feedback.resource_id,
				mandatory: feedback.mandatory,
				excluded: feedback.excluded,
				status: 'draft'
			})
			getFeedbacksCourses()
			getFeedbacksLessons()
		}
		catch (e) {
			console.error(e)
		}
	}

	return (
		<HeaderFooterLayout>
			<HelmetProvider>
				<Helmet>
					<title>
						Feedback
					</title>
				</Helmet>
			</HelmetProvider>
			<div className={styles.container}>
				<div className={styles.section}>
					<div className={styles.sectionInner}>
						<div className={styles.sectionHeader}>
							<div><h1>Feedback</h1><p>Consulta le valutazioni e le risposte degli studenti su corsi e lezioni.</p></div>
							<CreateButton onClick={() => { setEditingFeedback(null); setOpenDialog(true) }}>
								Crea modulo

							</CreateButton>
						</div>
						<div className={styles.tabs} role="group" aria-label="Tipo di feedback">{feedbackTypes.map((type, index) => <button key={type.id} type="button" aria-pressed={filter.id === type.id} onClick={() => onFilterStateChange(index)}>{type.label}</button>)}</div>
						{
							filter.id === FeedbackType.Course &&
							<FeedbacksCourses
								feedbacks={feedbacks[FeedbackType.Course]}
								feedbacksLoading={loading.course}
								onEdit={feedback => {
									setNewFeedback(null)
									setEditingFeedback(feedback)
								}}
								onDelete={feedback => askPermission(feedback)}
								onDuplicate={feedback => { duplicateFeedback(feedback) }}
							/>
						}
						{
							filter.id === FeedbackType.Lesson &&
							<FeedbacksLessons
								feedbacks={feedbacks[FeedbackType.Lesson]}
								feedbacksLoading={loading.lesson}
								onEdit={feedback => {
									setNewFeedback(null)
									setEditingFeedback(feedback)
								}}
								onDelete={feedback => askPermission(feedback)}
							/>
						}
					</div>
				</div>
				<Dialog appearance="modern" style={{ width: "min(780px, 94vw)" }}
					open={openDialog}
					onClose={() => {
						setOpenDialog(false)
						setEditingFeedback(null)
						setSaveStatus(null)
						setDisableSave(false)
					}}
					title={!editingFeedback ? "Aggiungi modulo feedback" : "Modifica modulo feedback"}
					contentMaxHeight="80dvh"
					action={
						<div className={styles.dialogActions}>
							{
								editingFeedback &&
								<Button
									accentColor={"var(--sf-red)"}
									additionalClass={styles.primaryAction}
									onClick={() => {
										setOpenDialog(false)
										askPermission(editingFeedback)
									}}>
									ELIMINA
								</Button>
							}
							<Button
								additionalClass={styles.primaryAction}
								onClick={save}
								status={saveStatus}
								disabled={disableSave}>
								SALVA
							</Button>
						</div>
					}
				>
					<FeedbackEditor feedback={editingFeedback} courses={courses} onValidityChange={v => setDisableSave(!v)} onChange={(f) => {
						setNewFeedback(f)
					}} />
				</Dialog>
				<AlertDialog
					open={alertDialog.open}
					title={alertDialog.title}
					text={alertDialog.text}
					actions={alertDialog.actions}
					status={alertDialog.status}
					onClose={closeAlert}
				/>
			</div >
		</HeaderFooterLayout >
	)
}

export default Feedbacks
